<template>
  <div class="chatbot-page">
    <div class="chatbot-container">
      <div class="chatbot-header">
        <b-avatar
          variant="primary"
          size="36"
        >
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="2"
              r="1.5"
              fill="white"
            />
            <line
              x1="12"
              y1="3.5"
              x2="12"
              y2="6"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <rect
              x="3"
              y="6"
              width="18"
              height="14"
              rx="3"
              fill="white"
            />
            <rect
              x="6.5"
              y="10"
              width="3.5"
              height="3.5"
              rx="1"
              fill="#7367f0"
            />
            <rect
              x="14"
              y="10"
              width="3.5"
              height="3.5"
              rx="1"
              fill="#7367f0"
            />
            <rect
              x="7"
              y="15.5"
              width="10"
              height="2"
              rx="1"
              fill="#7367f0"
            />
          </svg>
        </b-avatar>
        <div class="chatbot-header-info">
          <h5 class="mb-0">
            Chatbot
          </h5>
          <small
            class="text-muted"
            :class="{
              'text-success': connectionStatus === 'online',
              'text-warning': connectionStatus === 'connecting',
              'text-danger': connectionStatus === 'offline',
            }"
          >
            {{ statusLabel }}
          </small>
        </div>
        <div class="chatbot-header-ai-toggle ml-auto">
          <b-form-select
            v-model="chat_mode"
            :options="chatModeOptions"
            size="sm"
            class="chatbot-mode-select mr-1"
          />
          <b-form-checkbox
            v-model="useAi"
            switch
            size="sm"
            class="chatbot-ai-switch"
          >
            <span class="chatbot-ai-label">Использовать ИИ</span>
          </b-form-checkbox>
        </div>
      </div>

      <div
        ref="chatLog"
        class="chatbot-messages"
      >
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="chatbot-message-row"
          :class="{ 'chatbot-message-row--user': msg.sender === 'user' }"
        >
          <b-avatar
            v-if="msg.sender === 'bot'"
            variant="primary"
            size="32"
            class="chatbot-avatar"
          >
            <svg
              viewBox="0 0 24 24"
              width="19"
              height="19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="2"
                r="1.5"
                fill="white"
              />
              <line
                x1="12"
                y1="3.5"
                x2="12"
                y2="6"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <rect
                x="3"
                y="6"
                width="18"
                height="14"
                rx="3"
                fill="white"
              />
              <rect
                x="6.5"
                y="10"
                width="3.5"
                height="3.5"
                rx="1"
                fill="#7367f0"
              />
              <rect
                x="14"
                y="10"
                width="3.5"
                height="3.5"
                rx="1"
                fill="#7367f0"
              />
              <rect
                x="7"
                y="15.5"
                width="10"
                height="2"
                rx="1"
                fill="#7367f0"
              />
            </svg>
          </b-avatar>
          <div
            class="chatbot-bubble"
            :class="msg.sender === 'user' ? 'chatbot-bubble--user' : 'chatbot-bubble--bot'"
          >
            <div
              v-if="msg.format === 'markdown'"
              class="chatbot-markdown"
              v-html="renderMarkdown(msg.text)"
            />
            <template v-else>
              {{ msg.text }}
            </template>
            <button
              class="chatbot-copy-btn"
              :title="msg.copied ? 'Скопировано!' : 'Копировать'"
              @click="copyMessage(index)"
            >
              <feather-icon
                :icon="msg.copied ? 'CheckIcon' : 'CopyIcon'"
                size="14"
              />
            </button>
          </div>
          <b-avatar
            v-if="msg.sender === 'user'"
            variant="info"
            size="32"
            :text="userInitials"
            class="chatbot-avatar"
          />
        </div>

        <div
          v-if="isWaiting"
          class="chatbot-message-row"
        >
          <b-avatar
            variant="primary"
            size="32"
            class="chatbot-avatar"
          >
            <svg
              viewBox="0 0 24 24"
              width="19"
              height="19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="2"
                r="1.5"
                fill="white"
              />
              <line
                x1="12"
                y1="3.5"
                x2="12"
                y2="6"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <rect
                x="3"
                y="6"
                width="18"
                height="14"
                rx="3"
                fill="white"
              />
              <rect
                x="6.5"
                y="10"
                width="3.5"
                height="3.5"
                rx="1"
                fill="#7367f0"
              />
              <rect
                x="14"
                y="10"
                width="3.5"
                height="3.5"
                rx="1"
                fill="#7367f0"
              />
              <rect
                x="7"
                y="15.5"
                width="10"
                height="2"
                rx="1"
                fill="#7367f0"
              />
            </svg>
          </b-avatar>
          <div class="chatbot-bubble chatbot-bubble--bot chatbot-typing">
            <span class="chatbot-typing-dot" />
            <span class="chatbot-typing-dot" />
            <span class="chatbot-typing-dot" />
          </div>
        </div>
      </div>

      <div
        v-if="contextObject || contextEntity"
        class="chatbot-statusbar"
      >
        <feather-icon
          icon="BoxIcon"
          size="14"
          class="chatbot-statusbar-icon"
        />
        <span class="chatbot-statusbar-label">Контекст:</span>
        <template v-if="contextObject">
          <router-link
            :to="`/cubus/object/${contextObject.objectId}`"
            class="chatbot-statusbar-link"
          >
            {{ contextObject.objectName }}
          </router-link>
          <span class="chatbot-statusbar-id">#{{ contextObject.objectId }}</span>
        </template>
        <template v-if="contextEntity">
          <feather-icon
            icon="ChevronRightIcon"
            size="14"
            class="chatbot-statusbar-separator"
          />
          <span class="chatbot-statusbar-entity">{{ contextEntity.entityName }}</span>
          <span class="chatbot-statusbar-id">#{{ contextEntity.entityId }}</span>
        </template>
      </div>

      <div class="chatbot-input-area">
        <b-form
          class="chatbot-form"
          @submit.prevent="sendMessage"
        >
          <b-form-input
            ref="chatInput"
            v-model="inputText"
            placeholder="Введите сообщение..."
            autocomplete="off"
            @keydown.up="historyUp"
            @keydown.down="historyDown"
          />
          <b-button
            v-if="isSpeechSupported"
            :variant="isRecording ? 'danger' : 'outline-secondary'"
            :class="{ 'chatbot-mic-recording': isRecording }"
            @click="toggleVoiceInput"
          >
            <feather-icon
              icon="MicIcon"
              size="16"
            />
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="!inputText.trim()"
          >
            <feather-icon
              icon="SendIcon"
              size="16"
            />
          </b-button>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BAvatar, BForm, BFormInput, BButton, BFormCheckbox, BFormSelect,
} from 'bootstrap-vue'
import { marked } from 'marked'
import useCubus from '@/cubus/services/useCubus'
import { getConfig } from '@/libs/axios'
import { parseCommand, HELP_TEXT } from '@/cubus/services/chatCommands'

const MAX_RECONNECT_ATTEMPTS = 10
const RECONNECT_INTERVAL = 3000

export default {
  name: 'CbsChatbot',
  components: {
    BAvatar,
    BForm,
    BFormInput,
    BButton,
    BFormCheckbox,
    BFormSelect,
  },
  data() {
    return {
      inputText: '',
      messages: [
        { sender: 'bot', text: 'Здравствуйте! Чем могу помочь?' },
      ],
      connectionStatus: 'connecting',
      ws: null,
      reconnectAttempts: 0,
      reconnectTimer: null,
      isRecording: false,
      recognition: null,
      wasVoiceInput: false,
      commandHistory: [],
      historyIndex: -1,
      contextObject: null,
      contextEntity: null,
      useAi: false,
      isWaiting: false,
      chat_mode: 'chat',
      chatModeOptions: [
        { value: 'chat', text: 'Chat' },
        { value: 'debug', text: 'Debug' },
      ],
    }
  },
  computed: {
    userInitials() {
      const session = useCubus.getSession()
      if (session && session.user && session.user.name) {
        return session.user.name.substring(0, 2).toUpperCase()
      }
      return 'ME'
    },
    statusLabel() {
      const labels = {
        online: 'Online',
        connecting: 'Connecting...',
        offline: 'Offline',
      }
      return labels[this.connectionStatus] || 'Offline'
    },
    isSpeechSupported() {
      return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
    },
  },
  created() {
    useCubus.setPageTitle('Chatbot')
  },
  mounted() {
    this.connectWebSocket()
    this.focusInput()
  },
  beforeDestroy() {
    this.closeWebSocket()
    if (this.recognition) {
      this.recognition.abort()
      this.recognition = null
    }
  },
  methods: {
    getWsUrl() {
      const baseURL = getConfig()
      const url = new URL('/ws/chat', baseURL)
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      return url.toString()
    },
    getToken() {
      // PROP-086: токен из Vuex-сессии (в памяти), а не из localStorage. Нужен явно, потому
      // что AI-сервис живёт на другом origin, куда httpOnly-cookie не приложится.
      const s = this.$store.getters['cubus-store/SESSION']
      return (s && s.token) || ''
    },
    connectWebSocket() {
      this.closeWebSocket()
      this.connectionStatus = 'connecting'

      const url = this.getWsUrl()
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this.connectionStatus = 'online'
        this.reconnectAttempts = 0
        this.ws.send(JSON.stringify({
          type: 'auth',
          token: this.getToken(),
        }))
      }

      this.ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'message') {
            this.isWaiting = false
            this.messages.push({ sender: 'bot', text: data.text, format: data.format })
            if (data.context) {
              if (data.context.objectId) {
                this.contextObject = { objectId: data.context.objectId, objectName: data.context.objectName }
              } else {
                this.contextObject = null
              }
              if (data.context.entityId) {
                this.contextEntity = { entityId: data.context.entityId, entityName: data.context.entityName }
              } else {
                this.contextEntity = null
              }
            }
            this.scrollToBottom()
          } else if (data.type === 'error') {
            this.isWaiting = false
            this.messages.push({ sender: 'bot', text: `Ошибка: ${data.text}` })
            this.scrollToBottom()
          }
        } catch {
          // ignore non-JSON messages
        }
      }

      this.ws.onclose = () => {
        this.connectionStatus = 'offline'
        this.scheduleReconnect()
      }

      this.ws.onerror = () => {
        this.connectionStatus = 'offline'
      }
    },
    closeWebSocket() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      if (this.ws) {
        this.ws.onclose = null
        this.ws.onerror = null
        this.ws.close()
        this.ws = null
      }
    },
    scheduleReconnect() {
      if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return
      this.reconnectAttempts += 1
      this.reconnectTimer = setTimeout(() => {
        this.connectWebSocket()
      }, RECONNECT_INTERVAL)
    },
    toggleVoiceInput() {
      if (this.isRecording) {
        this.recognition.stop()
        return
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this.recognition = new SpeechRecognition()
      this.recognition.lang = 'ru-RU'
      this.recognition.interimResults = false
      this.recognition.continuous = false

      this.recognition.onresult = event => {
        const { transcript } = event.results[0][0]
        this.inputText += (this.inputText ? ' ' : '') + transcript
        this.wasVoiceInput = true
        this.$nextTick(() => {
          this.focusInput()
        })
      }

      this.recognition.onend = () => {
        this.isRecording = false
        this.$nextTick(() => {
          this.focusInput()
        })
      }

      this.recognition.onerror = () => {
        this.isRecording = false
      }

      this.recognition.start()
      this.isRecording = true
    },
    capitalizeWords(str) {
      return str.replace(/\S+/g, word => word.charAt(0).toUpperCase() + word.slice(1))
    },
    formatCommand(text) {
      const match = text.match(/^(создай\s+объект\s+)(.+)$/i)
      if (match) {
        return match[1] + this.capitalizeWords(match[2])
      }
      return text
    },
    getHelpText() {
      return `${HELP_TEXT}

**Текстовые команды:**
- **привет** — приветствие
- **кто я** — информация о пользователе
- **где я** — текущий домен
- **статус** — статус сессии
- **открой ххх** — найти объект по имени
- **сущность ххх** — описание сущности по ID
- **покажи объект ххх** — информация об объекте по ID
- **выбери ххх** — выбрать объект из результатов поиска
- **создай ааа ббб** — создать объект (ааа = тип, ббб = имя)
- **добавь атрибут ааа** — добавить атрибут в текущий объект
- **добавь записи в ааа** — массовое добавление записей в объект
- **покажи атрибуты** — атрибуты текущего объекта
- **покажи параметры формы** — параметры формы текущего объекта
- **обнови параметры формы {...}** — обновить параметры формы
- **контекст** — показать текущий объект
- **сделай деплой** — деплой текущего домена`
    },
    handleLocalCommand(text) {
      const result = parseCommand(text)
      if (!result.handled) return false

      if (result.localReply === 'help') {
        this.messages.push({ sender: 'bot', text: this.getHelpText(), format: 'markdown' })
        this.scrollToBottom()
        return true
      }
      if (result.localReply === 'clear') {
        this.messages = [{ sender: 'bot', text: 'Контекст очищен.' }]
        this.contextObject = null
        this.contextEntity = null
        this.scrollToBottom()
        this.sendSilent('/clear')
        return true
      }
      if (result.localReply) {
        this.messages.push({ sender: 'bot', text: result.localReply })
        this.scrollToBottom()
        return true
      }
      if (result.serverText) {
        this.sendToServer(result.serverText)
      }
      return true
    },
    sendMessage() {
      const text = this.formatCommand(this.inputText.trim())
      if (!text) return

      const resumeVoice = this.wasVoiceInput
      this.commandHistory.unshift(text)
      this.historyIndex = -1
      this.messages.push({ sender: 'user', text })
      this.inputText = ''
      this.wasVoiceInput = false
      this.scrollToBottom()

      if (this.handleLocalCommand(text)) {
        if (resumeVoice && this.isSpeechSupported) {
          this.$nextTick(() => { this.toggleVoiceInput() })
        }
        return
      }

      this.sendToServer(text)

      if (resumeVoice && this.isSpeechSupported) {
        this.$nextTick(() => {
          this.toggleVoiceInput()
        })
      }
    },
    sendSilent(text) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
      this.ws.send(JSON.stringify({
        type: 'message',
        to: 'chatbot',
        text,
        silent: true,
        token: this.getToken(),
      }))
    },
    sendToServer(text) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.messages.push({
          sender: 'bot',
          text: 'Нет подключения к серверу. Попробуйте позже.',
        })
        this.scrollToBottom()
        return
      }

      this.isWaiting = true
      this.scrollToBottom()
      this.ws.send(JSON.stringify({
        type: 'message',
        to: 'chatbot',
        text,
        useAi: this.useAi,
        mode: this.chat_mode,
        token: this.getToken(),
      }))
    },
    copyMessage(index) {
      const msg = this.messages[index]
      if (!msg) return
      navigator.clipboard.writeText(msg.text).then(() => {
        this.$set(this.messages, index, { ...msg, copied: true })
        setTimeout(() => {
          this.$set(this.messages, index, { ...msg, copied: false })
        }, 1500)
      })
    },
    renderMarkdown(text) {
      if (!text) return ''
      return marked.parse(text)
    },
    historyUp(e) {
      if (this.commandHistory.length === 0) return
      if (this.historyIndex < this.commandHistory.length - 1) {
        e.preventDefault()
        this.historyIndex += 1
        this.inputText = this.commandHistory[this.historyIndex]
      }
    },
    historyDown(e) {
      if (this.historyIndex < 0) return
      e.preventDefault()
      if (this.historyIndex > 0) {
        this.historyIndex -= 1
        this.inputText = this.commandHistory[this.historyIndex]
      } else {
        this.historyIndex = -1
        this.inputText = ''
      }
    },
    focusInput() {
      const input = this.$refs.chatInput
      if (input) {
        input.focus()
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatLog
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.chatbot-page {
  height: calc(100vh - 160px);
  display: flex;
}

.chatbot-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--white, #fff);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.chatbot-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #ebe9f1;
}

.chatbot-header-info h5 {
  font-size: 15px;
}

.chatbot-header-ai-toggle {
  display: flex;
  align-items: center;
}

.chatbot-mode-select {
  width: auto;
  min-width: 90px;
  font-size: 13px;
}

.chatbot-ai-switch {
  display: flex;
  align-items: center;
}

.chatbot-ai-label {
  font-size: 13px;
  color: #6e6b7b;
  white-space: nowrap;
}

.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chatbot-message-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  &--user {
    justify-content: flex-end;
  }
}

.chatbot-avatar {
  flex-shrink: 0;
}

.chatbot-typing {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 12px 16px;
}

.chatbot-typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #aaa;
  animation: typing-bounce 1.2s ease-in-out infinite;

  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
}

@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-5px); opacity: 1; }
}

.chatbot-bubble {
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
  word-wrap: break-word;
  white-space: pre-wrap;
  position: relative;

  &:hover .chatbot-copy-btn {
    opacity: 1;
  }

  &--bot {
    background: #f3f2f5;
    color: #333;
    border-bottom-left-radius: 4px;
  }

  &--user {
    background: #7367f0;
    color: #fff;
    border-bottom-right-radius: 4px;
  }
}

.chatbot-copy-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  background: none;
  border: none;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  color: inherit;
  transition: opacity 0.15s, background 0.15s;
  line-height: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
}

.chatbot-markdown ::v-deep {
  p:last-child {
    margin-bottom: 0;
  }

  pre {
    background: rgba(0, 0, 0, 0.05);
    padding: 8px 12px;
    border-radius: 6px;
    overflow-x: auto;
  }

  code {
    font-size: 13px;
  }

  ul, ol {
    padding-left: 20px;
    margin-bottom: 0.5em;
  }

  table {
    border-collapse: collapse;
    margin: 0.5em 0;

    th, td {
      border: 1px solid #ddd;
      padding: 4px 8px;
    }
  }
}

.chatbot-statusbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 20px;
  border-top: 1px solid #ebe9f1;
  background: #f8f8f8;
  font-size: 13px;
  color: #6e6b7b;
}

.chatbot-statusbar-icon {
  flex-shrink: 0;
  color: #7367f0;
}

.chatbot-statusbar-label {
  font-weight: 500;
}

.chatbot-statusbar-link {
  color: #7367f0;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

.chatbot-statusbar-id {
  color: #b9b9c3;
  font-size: 12px;
}

.chatbot-statusbar-separator {
  color: #b9b9c3;
  flex-shrink: 0;
}

.chatbot-statusbar-entity {
  color: #28c76f;
  font-weight: 600;
}

.chatbot-input-area {
  padding: 16px 20px;
  border-top: 1px solid #ebe9f1;
}

.chatbot-form {
  display: flex;
  gap: 8px;
}

.chatbot-mic-recording {
  animation: mic-pulse 1s ease-in-out infinite;
}

@keyframes mic-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.dark-layout {
  .chatbot-container {
    background: #283046;
  }

  .chatbot-header,
  .chatbot-input-area,
  .chatbot-statusbar {
    border-color: #3b4253;
  }

  .chatbot-statusbar {
    background: #242b3d;
    color: #b4b7bd;
  }

  .chatbot-bubble--bot {
    background: #3b4253;
    color: #d0d2d6;
  }
}
</style>
